import { useState, useEffect } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiCheck, FiX, FiClock, FiDatabase } from 'react-icons/fi';
import api from '../utils/api';

const Settings = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [autoSyncStatus, setAutoSyncStatus] = useState(null);
  const [loadingSyncStatus, setLoadingSyncStatus] = useState(true);

  // Fetch sync status on mount and after refresh
  const fetchSyncStatus = async () => {
    try {
      setLoadingSyncStatus(true);
      const response = await api.get('/sync/status');
      setSyncStatus(response.data.data);
      setAutoSyncStatus(response.data.autoSync);
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    } finally {
      setLoadingSyncStatus(false);
    }
  };

  useEffect(() => {
    fetchSyncStatus();
  }, []);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const handleForceRefresh = () => {
    setShowConfirmation(false);
    setIsRefreshing(true);
    setLogs([]);
    setResult(null);

    // Get the auth token
    const token = localStorage.getItem('token');
    
    // Use EventSource for real-time streaming
    const eventSource = new EventSource(
      `/api/sync/force-refresh?token=${encodeURIComponent(token)}`
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'log') {
          addLog(data.message, data.logType);
        } else if (data.type === 'result') {
          if (data.success) {
            setResult({
              success: true,
              message: 'Data refresh completed successfully!',
              synced: data.synced
            });
            // Refetch sync status after successful refresh
            fetchSyncStatus();
          } else {
            setResult({
              success: false,
              message: `Refresh failed: ${data.error}`
            });
          }
          eventSource.close();
          setIsRefreshing(false);
        }
      } catch (e) {
        console.error('Error parsing SSE data:', e);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE Error:', error);
      addLog('Connection error - please try again', 'error');
      setResult({
        success: false,
        message: 'Connection lost during refresh'
      });
      eventSource.close();
      setIsRefreshing(false);
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-gray-600">
          Manage application settings and data synchronization
        </p>
      </div>

      {/* Last Sync Status */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiClock className="text-blue-600" />
            Last Sync Status
          </h2>
          <button
            onClick={fetchSyncStatus}
            disabled={loadingSyncStatus}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <FiRefreshCw className={loadingSyncStatus ? 'animate-spin' : ''} size={14} />
            Refresh
          </button>
        </div>

        {loadingSyncStatus ? (
          <div className="text-gray-500 text-sm">Loading sync status...</div>
        ) : syncStatus && syncStatus.length > 0 ? (
          <div className="space-y-4">
            {/* Most Recent Sync */}
            {(() => {
              const mostRecent = syncStatus.reduce((latest, current) => {
                const currentDate = new Date(current.lastSyncAt);
                const latestDate = latest ? new Date(latest.lastSyncAt) : new Date(0);
                return currentDate > latestDate ? current : latest;
              }, null);
              
              if (mostRecent) {
                const lastSyncDate = new Date(mostRecent.lastSyncAt);
                const now = new Date();
                const diffMs = now - lastSyncDate;
                const diffMins = Math.floor(diffMs / 60000);
                const diffHours = Math.floor(diffMins / 60);
                const diffDays = Math.floor(diffHours / 24);
                
                let timeAgo;
                if (diffMins < 1) timeAgo = 'Just now';
                else if (diffMins < 60) timeAgo = `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
                else if (diffHours < 24) timeAgo = `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
                else timeAgo = `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
                
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-600 font-medium">Last Full Sync</p>
                    <p className="text-2xl font-bold text-blue-900">{timeAgo}</p>
                    <p className="text-sm text-blue-700 mt-1">
                      {lastSyncDate.toLocaleDateString('en-US', { 
                        weekday: 'long',
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                );
              }
              return null;
            })()}

            {/* Entity Breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {syncStatus.map((entity) => (
                <div key={entity.entityType} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <FiDatabase className="text-gray-400" size={14} />
                    <p className="text-xs text-gray-500 capitalize">
                      {entity.entityType.replace('_', ' ')}
                    </p>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{entity.recordCount}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(entity.lastSyncAt).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-gray-500 text-sm">
            No sync data available. Run a sync to populate this information.
          </div>
        )}

        {/* Auto-Sync Status */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <span className="font-medium text-gray-900">Automatic Sync: Active</span>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Data automatically syncs from Stripe every <strong>15 minutes</strong>. 
            You can also manually refresh using the Dashboard or Force Refresh below.
          </p>
          
          {autoSyncStatus && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-green-600 font-medium">Next Auto-Sync</p>
                <p className="text-green-900">
                  {autoSyncStatus.nextSyncAt 
                    ? new Date(autoSyncStatus.nextSyncAt).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'In ~15 minutes'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-600 font-medium">Status</p>
                <p className="text-gray-900">
                  {autoSyncStatus.isAutoSyncing 
                    ? '🔄 Syncing now...' 
                    : '✅ Idle - waiting for next sync'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Force Refresh Section */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Force Refresh Data
        </h2>
        <p className="text-gray-600 mb-4">
          Completely wipe the local database and re-sync all data from Stripe. 
          Use this if you notice data inconsistencies or want a fresh start.
        </p>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-800">What this does:</h3>
              <ul className="mt-2 text-sm text-amber-700 list-disc list-inside space-y-1">
                <li>Deletes all local data (customers, subscriptions, invoices, checkout sessions, refunds)</li>
                <li>Fetches fresh data directly from your Stripe account</li>
                <li>Rebuilds all commission and metrics calculations</li>
                <li>This does NOT affect your actual Stripe data - it's safe!</li>
              </ul>
              <div className="mt-3 p-2 bg-green-100 border border-green-200 rounded text-green-800 text-sm">
                <strong>Note:</strong> Your manually edited source/salesperson overrides will be preserved.
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowConfirmation(true)}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} />
          {isRefreshing ? 'Refreshing...' : 'Force Refresh All Data'}
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <FiAlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Full Data Refresh
              </h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Are you sure you want to wipe the local database and re-sync all data from Stripe?
            </p>
            
            <p className="text-sm text-gray-500 mb-4">
              This process may take a few minutes depending on the amount of data in your Stripe account.
              Your actual Stripe data will not be affected.
            </p>

            <p className="text-sm text-green-700 bg-green-50 p-2 rounded mb-4">
              Your manually edited source overrides will be preserved.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleForceRefresh}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Yes, Refresh All Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Logs */}
      {logs.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Refresh Progress
          </h2>
          <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm max-h-80 overflow-y-auto">
            {logs.map((log, index) => (
              <div 
                key={index} 
                className={`py-1 ${
                  log.type === 'error' ? 'text-red-400' :
                  log.type === 'success' ? 'text-green-400' :
                  'text-gray-300'
                }`}
              >
                <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
              </div>
            ))}
            {isRefreshing && (
              <div className="py-1 text-gray-400 animate-pulse">
                Processing...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Result Message */}
      {result && (
        <div className={`card border-2 ${result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
          <div className="flex items-center gap-3">
            {result.success ? (
              <FiCheck className="w-6 h-6 text-green-600" />
            ) : (
              <FiX className="w-6 h-6 text-red-600" />
            )}
            <div>
              <h3 className={`font-semibold ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                {result.message}
              </h3>
              {result.success && result.synced && (
                <p className="text-sm text-green-700 mt-1">
                  Synced: {result.synced.customers} customers, {result.synced.subscriptions} subscriptions,
                  {' '}{result.synced.checkoutSessions} checkout sessions, {result.synced.invoices} invoices,
                  {' '}{result.synced.refunds} refunds
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Other Settings */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Application Info
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Cache Duration</p>
            <p className="font-medium text-gray-900">5 minutes</p>
          </div>
          <div>
            <p className="text-gray-500">Commission Rate</p>
            <p className="font-medium text-gray-900">10%</p>
          </div>
          <div>
            <p className="text-gray-500">Data Source</p>
            <p className="font-medium text-gray-900">PostgreSQL + Stripe API</p>
          </div>
          <div>
            <p className="text-gray-500">Environment</p>
            <p className="font-medium text-gray-900">Development</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;

